import React, {FC, ReactNode, useMemo} from 'react';
import styled from 'styled-components';

import {buildHoverParentClassName} from '../../../../libs/shared/helpers/src/browser/hoverHelpers';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {isValidEmailAddress} from '../../../helpers/emailHelpers';
import {CardModel} from '../../../models/api/cardModel';
import {VisualContactModel} from '../../../models/api/visualContactModel';
import {Skeleton} from '../../common/skeleton';
import {MultilineTooltipOverflowCondition} from '../../common/tooltips/multilineTooltipOverflowCondition';
import {ContactCardActionsWrapper} from '../common/contactCardActionsWrapper';
import {ContactCardCopyButton} from '../common/contactCardCopyButton';
import {ContactCardHeaderPrivateCardIndicator} from './contactCardHeaderPrivateCardIndicator';

/*
 * Props.
 */

interface ContactCardHeaderNameProps {
  visualContact: VisualContactModel;
  card?: CardModel;
  isLoading: boolean;
  isUserVisitor: boolean;
}

/*
 * Style.
 */

const StyledNameWrapperDiv = styled.div`
  position: relative;
  display: flex;
  flex-flow: row;
  font-weight: ${fontWeights.bold};
  font-size: ${fontSizes.veryLarge};
  color: ${(p) => p.theme.greys.shade80};
  gap: 4px;
`;

/*
 * Component.
 */

export const ContactCardHeaderName: FC<ContactCardHeaderNameProps> = (props) => {
  const {isLoading, visualContact, card, isUserVisitor} = props;
  const isMultilineAvailable = Boolean(!card?.accountId) || isUserVisitor;
  const displayName = computeNameFromContactAndCard(visualContact, card);
  const isDisplayNameEmail = useMemo(() => isValidEmailAddress(displayName), [displayName]);

  if (isLoading) {
    return (
      <StyledNameWrapperDiv>
        <Skeleton height="20px" />
      </StyledNameWrapperDiv>
    );
  }
  return (
    <StyledNameWrapperDiv className={buildHoverParentClassName()}>
      <TooltipCoordinator render={() => displayName} placement="top">
        {renderDisplayNameWithOverflowCondition(isMultilineAvailable, displayName, isDisplayNameEmail)}
      </TooltipCoordinator>
      <ContactCardHeaderPrivateCardIndicator card={card} />
      <ContactCardActionsWrapper>
        <ContactCardCopyButton copyValue={displayName} />
      </ContactCardActionsWrapper>
    </StyledNameWrapperDiv>
  );
};

/*
 * Helpers.
 */

function renderDisplayNameWithOverflowCondition(
  isMultilineAvailable: boolean,
  displayName: ReactNode,
  isDisplayNameEmail: boolean,
) {
  // If we only have an email address, we should treat it as a single line.
  if (isMultilineAvailable && !isDisplayNameEmail) {
    return <MultilineTooltipOverflowCondition>{displayName}</MultilineTooltipOverflowCondition>;
  }
  return <TooltipOverflowCondition>{displayName}</TooltipOverflowCondition>;
}

function computeNameFromContactAndCard(visualContact: VisualContactModel, card?: CardModel) {
  return card?.name ?? visualContact.name ?? visualContact.contact.handle;
}
